<template>
  <div class="traceability-progress-bar">
    <div class="status-progress">
      <div class="status-progress__bar">
        <div class="status-progress__text">
          <div class="status-progress__status">
            <div class="status-text">
              <div>
                {{ currentStatusInfo.translated }} {{ checkDelay() ? '(atrasado)' : '' }}
              </div>

              <div>
                <button
                  v-if="isPathVisible"
                  type="button"
                  class="btn-path"
                  @click.stop="openMap"
                >
                  Ver trajeto

                  <svg-loader src="/static/images/icon-map-marker.svg" />
                </button>
              </div>
            </div>
          </div>

          <div v-if="currentStatusDate">
            {{ convertDataWithoutTimeZone(currentStatusDate) }}
          </div>
        </div>

        <div class="traceability-progress-bar-bar">
          <div
            class="traceability-progress-bar-bar-fill"
            :style="{
              width: getStatusWidth,
              backgroundColor: currentStatusInfo.color,
            }"
          ></div>
        </div>

        <div
          class="bar"
          style="display: none"
        >
          <div
            class="bar__fill"
            :style="{
              width: getStatusWidth,
              backgroundColor: getBarColor,
            }"
          />
          <div class="bar__dot bar__dot--1" />
          <div class="bar__dot bar__dot--2" />
          <div class="bar__dot bar__dot--3" />
          <div class="bar__dot bar__dot--4" />
          <div class="bar__dot bar__dot--5" />
          <div
            class="bar__dot bar__dot--6"
            :style="{
              backgroundColor:
                currentStatusInfo.progressPercent === '100%' &&
                !statuses.currentStatus === 'FINALIZADO'
                  ? currentStatusInfo.color
                  : '#fff',
            }"
            :class="{
              'bar__dot--progress-error':
                currentStatusInfo.progressPercent === '100%' &&
                !statuses.currentStatus === 'FINALIZADO',
            }"
          >
            <div
              v-if="
                currentStatusInfo.progressPercent === '100%' &&
                !statuses.currentStatus === 'FINALIZADO'
              "
            >
              <svg-loader src="/static/images/icon-close-small.svg" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="isOpen"
      class="status-list"
    >
      <div
        v-if="currentStatusInfo.progressPercent !== '100%'"
        class="status-item"
      >
        <div class="box-dot">
          <div class="status-item__line status-item__line--dotted" />
          <div
            class="status-item__dot"
            :style="{ backgroundColor: '#CCCCCC' }"
          />
        </div>
        <div class="status-item__content">
          <div class="status-item__text status-item__text--waiting">...</div>
          <div class="status-item__date" />
        </div>
      </div>
      <div
        v-for="(status, index) in statuses.events.slice().reverse()"
        :key="index"
        class="status-item"
      >
        <div class="box-dot">
          <div
            class="status-item__line"
            :class="{
              'status-item__line--dotted': index === 0,
              'status-item__line--no-border':
                index === 0 && currentStatusInfo.progressPercent === '100%',
            }"
          />
          <div
            class="status-item__dot"
            :style="{ backgroundColor: getStatusInfo(status.status).color }"
          />
        </div>
        <div class="status-item__content">
          <div class="status-item__text">
            {{ getStatusInfo(status.status).translated }}
            <md-tooltip
              md-delay="400"
              md-direction="top"
            >
              {{ getStatusInfo(status.status).translated }}
            </md-tooltip>
          </div>

          <div class="status-item__date">
            {{ convertDataWithoutTimeZone(status.date) }}
          </div>
        </div>
      </div>
    </div>

    <TraceabilityShowReadyExamsModal :html-for-download="htmlForDownload" />
  </div>
</template>

<script>
import Moment from 'moment'
import strings from '@/strings'

import resultPDFService from '../../api/resultsPDF'
import Spinner from '@/components/common/Spinner'
import notify from '@/api/notification'
import { mapGetters } from 'vuex'
import TraceabilityShowReadyExamsModal from './TraceabilityShowReadyExamsModal'

export default {
  components: {
    Spinner,
    TraceabilityShowReadyExamsModal,
  },
  props: {
    statuses: {
      type: Object,
      required: true,
      default: () => ({
        currentStatus: 'UNDEFINED_STATUS',
        currentStatusDate: null,
        dueDate: null,
      }),
      validator(value) {
        return value && typeof value.currentStatus === 'string'
      },
    },
    orderId: {
      type: String,
      required: false,
      default: null,
    },
    sample: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    currentStatusDate: {
      type: [Date, String],
    },
  },
  data() {
    return {
      isOpen: false,
      statusEnum: strings.traceabilityStatus,
      loading: false,
      htmlForDownload: new Uint8Array(),
    }
  },
  computed: {
    ...mapGetters(['profile']),
    currentStatusInfo() {
      if (!this.statuses || !this.statuses.currentStatus) {
        return {
          color: '#CCCCCC',
          progressPercent: '0%',
          translated: 'Status não informado',
        }
      }

      return (
        this.statusEnum[this.statuses.currentStatus] || {
          color: '#CCCCCC',
          progressPercent: '0%',
          translated: `Status desconhecido (${this.statuses.currentStatus})`,
        }
      )
    },
    getBarColor() {
      return this.currentStatusInfo.color
    },
    getStatusWidth() {
      const progressPercent = this.currentStatusInfo.progressPercent

      if (progressPercent === '100%' || progressPercent === '0%') {
        return progressPercent
      } else {
        const calcWidth = `calc(${progressPercent} + 5px)`
        return calcWidth
      }
    },
    isPathVisible() {
      return (
        this.statuses &&
        this.statuses.currentStatus &&
        (this.statuses.currentStatus === 'EM_TRANSITO' ||
          this.statuses.currentStatus === 'EM_TRANSITO_ENTRE_NTO')
      )
    },
  },
  methods: {
    getStatusInfo(statusKey) {
      if (!statusKey) {
        return {
          color: '#CCCCCC',
          progressPercent: '0%',
          translated: 'Status não informado',
        }
      }

      var data = this.statusEnum[statusKey] || {
        color: '#CCCCCC',
        progressPercent: '0%',
        translated: `Status desconhecido (${statusKey})`,
      }

      if (statusKey == 'TRIAGEM') {
        // Fix para não alterar no strings porque lá afeta a versão atual
        data.translated = 'Aguardando recebimento no NTO'
      }

      return data
    },
    convertDataWithoutTimeZone(date) {
      let dateTime = Moment.utc(date)

      const formattedDate = dateTime.format('DD/MM/YYYY')
      const formattedTime = dateTime.format('HH:mm')

      return formattedDate + ' às ' + formattedTime
    },
    openMap() {
      if (
        this.statuses.currentStatus === 'EM_TRANSITO' ||
        this.statuses.currentStatus === 'EM_TRANSITO_ENTRE_NTO'
      ) {
        this.$emit('openMap', {
          orderId: this.orderId,
          sample: this.sample,
        })
      }
    },
    checkDelay() {
      if (
        this.statuses.currentStatus === 'EM_EXECUCAO' &&
        (this.statuses.delayed || this.statuses.isDelayed)
      ) {
        return true
      }
      return false
    },
    seeReadyExamsByOrder(exam) {
      if (this.profile.defaulter) {
        notify({
          title: 'AVISO:',
          message:
            'Acesso bloqueado: Entre em contato com o time de atendimento através do 0800 643 8100 ou pelo e-mail: cobranca@dasa.movidesk.com',
        })
        return Promise.reject('Usuário inadimplente.')
      }

      if (this.loading) {
        return Promise.reject('Download já em andamento.')
      }

      this.loading = true
      return resultPDFService
        .downloadResultsPDF(exam.idOrder, [])
        .then(response => {
          this.loading = false
          return response.data
        })
        .catch(error => {
          this.loading = false
          notify({
            title: 'AVISO:',
            message: this.$strings.resultsPDFErrorsMessages.downloadFail,
          })
          throw error
        })
    },
    toggleDetails() {
      this.isOpen = !this.isOpen

      this.$emit('details-visibility-change', this.isOpen)
    },
  },
}
</script>

<style lang="scss" scoped>
.traceability-progress-bar {
  width: 100%;
  font-family: 'Exo 2';

  &-bar {
    background-color: #e6e6e6;
    width: 100%;
    height: 8px;
    border-radius: 4px;
    overflow: hidden;

    &-fill {
      height: 100%;
    }
  }

  .status-progress {
    display: flex;
    flex-wrap: nowrap;
    gap: 8px;

    &__text {
      display: flex;
      font-size: 14px;
      justify-content: space-between;
      margin-bottom: 8px;

      .status-text {
        font-size: 14px;
        max-width: 310px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        display: flex;
        gap: 8px;
      }

      .status-date {
        font-size: 14px;
        max-width: 310px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .status-text {
        font-weight: 600;
      }
    }

    &__status {
      display: flex;
      grid-gap: 5px;
    }

    &__btn-location {
      width: 32px !important;
      height: 32px;
      background-color: #801a54;
      border-radius: 50%;
      padding: 0 !important;
      pointer-events: none;

      &--is-clickable {
        pointer-events: inherit;
        cursor: pointer;
      }

      &--is-finished {
        background-color: #691043;
        cursor: pointer;

        .md-icon.header-title-info-icon {
          width: 30px !important;
          height: 30px !important;
        }

        .md-icon {
          width: 20px;
          min-width: 20px;
        }

        .loader {
          width: 24px;
          height: 24px;
          border-top: 0.5em solid rgba(255, 255, 255, 0.2);
          border-right: 0.5em solid rgba(255, 255, 255, 0.2);
          border-bottom: 0.5em solid rgba(255, 255, 255, 0.2);
          border-left: 0.5em solid #ffffff;
        }
      }

      .md-icon.header-title-info-icon {
        width: 20px;
        height: 20px;
      }
    }

    &__bar {
      flex: 1;
      align-content: center;

      .details-text {
        color: #701a54;
        text-align: right;
        cursor: pointer;
      }
    }

    .box-dot {
      align-items: center;
    }
  }

  .status-list {
    color: #363636;
    margin-top: 12px;
    padding-left: 10px;

    .status-item {
      display: flex;
      align-items: flex-end;
      margin-bottom: 10px;
      height: 35px;
      gap: 24px;

      &__line {
        width: 0px;
        position: absolute;
        height: 45px;
        border-left: 1px solid #ccc;

        &--dotted {
          border: 1px dashed #ccc;
        }

        &--no-border {
          border: none !important;
        }
      }

      &__dot {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        position: relative;
      }

      &__content {
        display: flex;
        flex: 1;
        line-height: 13px;
        gap: 8px;
        justify-content: space-between;
      }

      &__text {
        font-weight: 600;
        max-width: 200px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        &--waiting {
          color: #ccc;
        }
      }

      &__date {
        font-weight: 400;
      }
    }
  }

  .bar {
    position: relative;
    height: 10px;
    background-color: #ccc;
    border-radius: 10px;
    margin: 12px 0;

    &__fill {
      width: calc(72.6% - 5px);
      height: 100%;
      border-radius: 12px;
    }

    &__dot {
      position: absolute;
      top: 1px;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background-color: #ffffff;
      transform: translateX(-50%);

      &--1 {
        left: 12%;
      }

      &--2 {
        left: 30%;
      }

      &--3 {
        left: 50%;
      }

      &--4 {
        left: 70%;
      }

      &--5 {
        left: 88%;
      }

      &--6 {
        left: calc(100% - 5px);
      }

      &--progress-error {
        width: 20px;
        height: 20px;
        top: -5px;
        border: 2px solid #fff;

        & > div {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 100%;

          .md-icon {
            width: 10px;
            min-width: 10px;
            height: 10px;
            min-height: 10px;
          }
        }
      }
    }
  }

  .box-dot {
    display: flex;
    justify-content: center;
    align-items: flex-end;
  }

  .header-title-info-icon {
    width: 20px;
    height: 20px;
    min-width: 20px;
    min-height: 20px;
    margin-bottom: 1px;
    color: #b7b7b7;
    cursor: pointer;

    &:hover {
      color: #7a7a7a;
    }
  }

  .btn-path {
    background-color: transparent;
    border: none;
    color: #7f1a54;
    cursor: pointer;
    font-family: 'Exo 2';
    font-weight: 400;
    font-size: 12px;
    padding: 0;
    width: fit-content;
    height: auto;

    .md-icon {
      min-width: auto;
      min-height: auto;
      width: 16px;
      height: 16px;
    }

    path {
      fill: currentColor;
    }
  }
}
</style>
